import React from 'react';

import {
	AdvancedType,
	BasicType,
	DynamicType,
	getAncestorPinnedListing,
	isBoundListingResolving,
	subscribeListings,
	resolveAttachedListingMergeTags,
	resolveBoundListing,
	t,
} from '@bay-email/core';
import { createPortal } from 'react-dom';
import {
	getEditorRoot,
	getShadowRoot,
	useBlock,
	useEditorContext,
	useEditorProps,
	useFocusBlockLayout,
	useFocusIdx,
} from '@bay-email/editor';
import { Tooltip } from '@arco-design/web-react';
import { BsArrowRepeat, BsExclamationTriangleFill } from 'react-icons/bs';
import { Toolbar } from './Toolbar';

function getCanvasBounds() {
	const canvas = getShadowRoot()?.querySelector(
		'div.shadow-container.bay-email-sync-scroll > div',
	) as HTMLElement | null;

	if (canvas) {
		return canvas.getBoundingClientRect();
	}

	return getEditorRoot()?.getBoundingClientRect() || null;
}

export function FocusTooltip() {
	const { focusBlock } = useBlock();
	const { focusBlockNode } = useFocusBlockLayout();
	const { focusIdx } = useFocusIdx();
	const { pageData } = useEditorContext();
	const { realUserProfile } = useEditorProps();
	const isSection =
		focusBlock?.type === BasicType.SECTION || focusBlock?.type === AdvancedType.SECTION;
	const isImageLikeBlock =
		focusBlock?.type === BasicType.IMAGE ||
		focusBlock?.type === AdvancedType.IMAGE ||
		focusBlock?.type === DynamicType.IMAGE;
	const explicitImageType = focusBlock?.data?.value?.imageType;
	// Stored already brace-wrapped ({{listing.image_data_0}}) by the attribute
	// panel's normalizeDynamicImageTagValue. Strip them the way the Image block's
	// own render does — rebuilding `{{...}}` around a value that still had its own
	// braces produced {{{{tag}}}}, which resolved to {{<url>}}. That still contains
	// "{{", so the resolved check below always failed and the fallback warning
	// showed over a perfectly resolved image.
	const dynamicImageExpression = String(
		focusBlock?.data?.value?.dynamicImageTag || '',
	).trim();
	const dynamicImageTag =
		dynamicImageExpression.match(/^\{\{\s*([^{}]+?)\s*\}\}$/)?.[1]?.trim() ||
		dynamicImageExpression.replace(/^\{\{|\}\}$/g, '').trim();
	const fallbackImageSrc = String(focusBlock?.attributes?.src || '').trim();
	const isDynamicImageSelected =
		Boolean(isImageLikeBlock) &&
		(focusBlock?.type === DynamicType.IMAGE ||
			explicitImageType === 'dynamic' ||
			(explicitImageType !== 'static' && Boolean(dynamicImageTag)));

	// Whether the field actually resolved is the same question the Image
	// block's own render() answers to choose between the real asset and the
	// fallback on canvas: bind against the listing this block (or its ancestor
	// listing column) is pinned to, and the current viewer's real profile.
	// An unresolved tag comes back unchanged (still containing `{{`) — that,
	// same as an empty result, means the canvas is showing the fallback.
	// Ancestor first, matching every block's render — a Listing Wrapper or listing
	// column WINS over a binding left on the block itself. Reading them the other
	// way round judged the icon against a different listing than the canvas drew.
	const selectedListing = isDynamicImageSelected
		? getAncestorPinnedListing(pageData, focusIdx) ||
			resolveBoundListing(focusBlock?.data?.value)
		: null;
	const resolvedDynamicValue =
		isDynamicImageSelected && dynamicImageTag
			? resolveAttachedListingMergeTags(`{{${dynamicImageTag}}}`, {
					listing: selectedListing,
					userProfile: realUserProfile,
				})
			: '';
	const isDynamicValueResolved =
		Boolean(resolvedDynamicValue) && !/\{\{/.test(resolvedDynamicValue);
	// The two kinds of dynamic image fail for different reasons, so they warn on
	// different rules. Treating them alike is what made this icon feel broken in
	// both directions — noisy on one, silent on the other.
	//
	//   listing.*  needs a listing attached, and the designer attaches it. Unresolved
	//              means the setup is unfinished no matter how good the fallback is,
	//              so always warn — the fallback is standing in for real data that is
	//              supposed to arrive.
	//   user.*     resolves from the signed-in profile. Nothing on this block can
	//              make an absent office logo appear; uploading a fallback IS the
	//              resolution. Once there is one, the block is done — no warning.
	//              With no fallback the dashed empty-state box already says so.
	const isListingBoundField = dynamicImageTag.startsWith('listing.');
	// A lookup takes seconds, and for all of them the field is unresolved and the
	// fallback is on screen — which used to be enough to raise the warning. The
	// icon was telling the truth about the pixels and lying about the design: it
	// warned about a listing that was on its way and then vanished when it
	// landed. "Unresolved" only means something is wrong once the fetch has come
	// back empty.
	const isListingResolving =
		isListingBoundField && isBoundListingResolving(pageData, focusIdx, focusBlock);
	const showDynamicImageWarning =
		isDynamicImageSelected &&
		!isDynamicValueResolved &&
		!isListingResolving &&
		(isListingBoundField || !fallbackImageSrc);

	const sectionOverlayBounds = React.useMemo(() => {
		if (!focusBlockNode || !isSection) return null;
		const rootRect = getCanvasBounds();
		if (!rootRect) return null;
		const blockRect = focusBlockNode.getBoundingClientRect();

		return {
			left: rootRect.left - blockRect.left,
			width: rootRect.width,
		};
	}, [focusBlockNode, isSection]);

	// The cache fills asynchronously and nothing else re-renders this overlay, so
	// without this the spinner and the warning both freeze on whichever state
	// they were in when the block was focused.
	const [, onListingsResolved] = React.useReducer((count: number) => count + 1, 0);
	React.useEffect(() => subscribeListings(onListingsResolved), []);

	if (!focusBlockNode || !focusBlock) return null;

	return (
		<>
			{createPortal(
				<div
					id='bay-email-extensions-InteractivePrompt-FocusTooltip'
					style={{
						position: 'absolute',
						width: sectionOverlayBounds?.width ?? '100%',
						height: '100%',
						pointerEvents: 'none',
						left: sectionOverlayBounds?.left ?? 0,
						top: 0,
						zIndex: 1,
					}}
				>
					<style>
						{`
                .email-block {
                  position: relative;
                }

                @keyframes bay-listing-spin {
                  to { transform: rotate(360deg); }
                }

            `}
					</style>
					{isListingResolving && (
						<Tooltip content={t('Loading listing data…')}>
							<div
								style={{
									position: 'absolute',
									top: 10,
									right: 10,
									width: 22,
									height: 22,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: '#75777f',
									filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))',
									pointerEvents: 'auto',
									zIndex: 10000,
								}}
							>
								<BsArrowRepeat
									size={18}
									style={{ animation: 'bay-listing-spin 1s linear infinite' }}
								/>
							</div>
						</Tooltip>
					)}

					{showDynamicImageWarning && (
						<Tooltip
						// "Showing fallback" is only true when there IS one — with no
						// fallback the block renders the dashed empty box, so claiming a
						// fallback was on screen made the tooltip lie about what the
						// designer was looking at.
						content={
							isListingBoundField
								? fallbackImageSrc
									? t('No listing data yet. Showing fallback.')
									: t('No listing data and no fallback image.')
								: t('Dynamic image has no data and no fallback image.')
						}
					>
							<div
								style={{
									position: 'absolute',
									top: 10,
									right: 10,
									width: 22,
									height: 22,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: '#f59e0b',
									filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))',
									pointerEvents: 'auto',
									cursor: 'help',
									zIndex: 10000,
								}}
							>
								<BsExclamationTriangleFill size={18} />
							</div>
						</Tooltip>
					)}

					{/* outline */}
					<div
						style={{
							position: 'absolute',
							fontSize: 14,
							zIndex: 2,
							left: 0,
							top: 0,
							width: '100%',
							height: '100%',
							outlineOffset: '-2px',
							outline: '2px solid var(--selected-color)',
						}}
					/>
					<div
						style={{
							position: 'absolute',
							fontSize: 14,
							zIndex: 3,
							left: 0,
							top: 0,
							width: '0%',
							height: '100%',
						}}
					>
						<Toolbar />
					</div>
				</div>,

				focusBlockNode,
			)}
		</>
	);
}
